import {Component, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from "@angular/core";
import {AsyncPipe, formatDate, NgClass} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {
    Edi997ConfigConnector,
    ExtractConfigEntry,
    ExtractConfigEntryDetails,
    ExtractConfigEntryRequest,
    ExtractConfigPreviewResponse,
    ExtractConfigPublishResponse
} from "@app/_models/api";
import {GlobalService} from "@app/_services/global.service";
import {AuthService} from "@app/_services/auth.service";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {ExtractConfigService} from "@app/_services/processing/extract-config.service";
import {BehaviorSubject, Observable} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

// Delivery types seen in extracts_cfg.json. Drives which fields apply per extract.
const EXTRACT_TYPE_SFTP = "sftp_connector";
const EXTRACT_TYPE_S3 = "s3_upload";
const EXTRACT_TYPE_S3_NO_SEND = "s3_upload_no_send";

// A single extract's editable fields — flattened s3_config to match EXTRACT_CONFIG_ENTRIES columns.
interface EntryForm {
    extract_key: FormControl<string>;
    type: FormControl<string>;
    name: FormControl<string>;
    sql: FormControl<string>;
    customer_code: FormControl<string>;
    field_separator: FormControl<string>;
    connector_id: FormControl<string>;
    remote_directory_path: FormControl<string>;
    remote_bucket: FormControl<string>;
    destination_account_id: FormControl<string>;
    destination_iam_role: FormControl<string>;
    is_enabled: FormControl<boolean>;
}

@Component({
    selector: "app-extract-config",
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        SortableDirective,
        NgClass,
        ModalConfirmComponent
    ],
    templateUrl: "./extract-config-component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./extract-config-component.scss"
})
export class ExtractConfigComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    savingData: boolean = false;
    total$: Observable<number>;
    entries$: Observable<ExtractConfigEntry[]>;

    // null = creating a new row; otherwise the id of the row being edited.
    editingId: string | null = null;

    availableConnectors: Edi997ConfigConnector[] = [];

    private _loadingDetails$ = new BehaviorSubject<boolean>(false);
    loadingDetails$ = this._loadingDetails$.asObservable();

    private _previewJson$ = new BehaviorSubject<string>("");
    previewJson$ = this._previewJson$.asObservable();
    previewCount = 0;

    editForm = new FormGroup<EntryForm>({
        extract_key: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        type: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        name: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        sql: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        customer_code: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        field_separator: new FormControl("", {nonNullable: true, validators: [Validators.required]}),
        connector_id: new FormControl("", {nonNullable: true}),
        remote_directory_path: new FormControl("", {nonNullable: true}),
        remote_bucket: new FormControl("", {nonNullable: true}),
        destination_account_id: new FormControl("", {nonNullable: true}),
        destination_iam_role: new FormControl("", {nonNullable: true}),
        is_enabled: new FormControl(true, {nonNullable: true})
    });

    protected readonly formatDate = formatDate;
    protected readonly extractTypes = [EXTRACT_TYPE_SFTP, EXTRACT_TYPE_S3, EXTRACT_TYPE_S3_NO_SEND];
    protected readonly EXTRACT_TYPE_SFTP = EXTRACT_TYPE_SFTP;
    protected readonly EXTRACT_TYPE_S3 = EXTRACT_TYPE_S3;

    @ViewChild("editModal") private editModal!: TemplateRef<any>;
    @ViewChild("previewModal") private previewModal!: TemplateRef<any>;
    @ViewChild("confirmationModal") private modalConfirmComponent!: ModalConfirmComponent;
    private editModalRef!: NgbModalRef;
    private previewModalRef!: NgbModalRef;

    constructor(
        public globalService: GlobalService,
        public authService: AuthService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        public service: ExtractConfigService,
        private modalService: NgbModal) {

        this.service.data = this.route.snapshot.data["resolvedData"].data;
        this.entries$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.refreshDataSource();
        this.loadConnectors();
    }

    get isEditing(): boolean {
        return this.editingId !== null;
    }

    onAdd(): void {
        this.editingId = null;
        this.resetForm();
        this.openEditModal();
    }

    onEdit(id: string): void {
        this.editingId = id;
        this.resetForm();
        this._loadingDetails$.next(true);
        this.openEditModal();

        this.apiService.getExtractConfigEntry(id).subscribe({
            next: (entry: ExtractConfigEntryDetails) => {
                this.editForm.setValue({
                    extract_key: entry.extractKey,
                    type: entry.type,
                    name: entry.name,
                    sql: entry.sql,
                    customer_code: entry.customerCode,
                    field_separator: entry.fieldSeparator,
                    connector_id: entry.connectorId ?? "",
                    remote_directory_path: entry.remoteDirectoryPath ?? "",
                    remote_bucket: entry.remoteBucket ?? "",
                    destination_account_id: entry.destinationAccountId ?? "",
                    destination_iam_role: entry.destinationIamRole ?? "",
                    is_enabled: entry.isEnabled
                });
                this._loadingDetails$.next(false);
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load extract details");
                this._loadingDetails$.next(false);
                this.editModalRef.dismiss();
                console.error("Error loading extract:", error);
            }
        });
    }

    onSave(): void {
        if (this.editForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        // Type-specific fields aren't covered by the base validators (they only apply to some types).
        const typeError = this.validateType();
        if (typeError) {
            this.toastService.showDanger("Invalid Data", typeError);
            return;
        }

        const v = this.editForm.getRawValue();

        // field_separator and sql are sent verbatim (a separator may be a space/tab; SQL formatting is
        // significant); id-like fields are trimmed. Connector / s3 fields are sent only for the type that
        // uses them so the row's unused columns stay null.
        const request: ExtractConfigEntryRequest = {
            extractKey: v.extract_key.trim(),
            type: v.type,
            name: v.name.trim(),
            sql: v.sql,
            customerCode: v.customer_code.trim(),
            fieldSeparator: v.field_separator,
            isEnabled: v.is_enabled,
            connectorId: v.type === EXTRACT_TYPE_SFTP ? v.connector_id.trim() : null,
            remoteDirectoryPath: v.type === EXTRACT_TYPE_SFTP ? v.remote_directory_path.trim() : null,
            remoteBucket: v.type === EXTRACT_TYPE_S3 ? v.remote_bucket.trim() : null,
            destinationAccountId: v.type === EXTRACT_TYPE_S3 ? v.destination_account_id.trim() : null,
            destinationIamRole: v.type === EXTRACT_TYPE_S3 ? v.destination_iam_role.trim() : null
        };

        this.savingData = true;
        this.globalService.showWaitOverlay(this.isEditing ? "Updating extract..." : "Creating extract...");

        const request$ = this.isEditing
            ? this.apiService.updateExtractConfigEntry(this.editingId!, request)
            : this.apiService.createExtractConfigEntry(request);

        request$.subscribe({
            next: () => {
                this.toastService.showSuccess("Success", `Extract ${this.isEditing ? "updated" : "created"} successfully`);
                this.editModalRef.close();
                this.savingData = false;
                this.globalService.hideWaitOverlay();
                this.refreshGrid();
            },
            error: (error) => {
                this.savingData = false;
                this.globalService.hideWaitOverlay();
                const message = error?.status === 409
                    ? "An extract with that key already exists."
                    : `Failed to ${this.isEditing ? "update" : "create"} extract`;
                this.toastService.showDanger("Error", message);
                console.error("Error saving extract:", error);
            }
        });
    }

    onDelete(entry: ExtractConfigEntry): void {
        this.modalConfirmComponent.open(`Delete extract "${entry.extractKey}"? It will disappear from the next published file.`);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.globalService.showWaitOverlay("Deleting extract...");
                this.apiService.deleteExtractConfigEntry(entry.id).subscribe({
                    next: () => {
                        this.toastService.showSuccess("Success", "Extract deleted");
                        this.refreshGrid();
                    },
                    error: (error) => {
                        this.globalService.hideWaitOverlay();
                        this.toastService.showDanger("Error", "Failed to delete extract");
                        console.error("Error deleting extract:", error);
                    }
                });
            },
            () => {
            }
        );
    }

    onPreview(): void {
        this._loadingDetails$.next(true);
        this._previewJson$.next("");
        this.previewModalRef = this.modalService.open(this.previewModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static"
        });

        this.apiService.previewExtractConfig().subscribe({
            next: (response: ExtractConfigPreviewResponse) => {
                this._previewJson$.next(response.configJson);
                this.previewCount = response.extractCount;
                this._loadingDetails$.next(false);
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to build preview");
                this._loadingDetails$.next(false);
                this.previewModalRef.dismiss();
                console.error("Error building preview:", error);
            }
        });
    }

    onPublish(): void {
        this.modalConfirmComponent.open("Publish all enabled extracts to the extracts S3 bucket?");
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.globalService.showWaitOverlay("Publishing extract config...");
                this.apiService.publishExtractConfig().subscribe({
                    next: (response: ExtractConfigPublishResponse) => {
                        this.globalService.hideWaitOverlay();
                        this.toastService.showSuccess("Success", response.message);
                    },
                    error: (error) => {
                        this.globalService.hideWaitOverlay();
                        this.toastService.showDanger("Error", "Failed to publish configuration");
                        console.error("Error publishing config:", error);
                    }
                });
            },
            () => {
            }
        );
    }

    closeEditModal(): void {
        this.modalConfirmComponent.confirmIfDirty(this.editForm, () => this.editModalRef.close());
    }

    closePreviewModal(): void {
        this.previewModalRef.close();
        this._previewJson$.next("");
    }

    longFormatDate(input: string): string {
        return this.formatDate(input + "Z", "yyyy-MM-dd, hh:mm:ss a", "en-US");
    }

    onSort({column, direction}: SortEvent): void {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }
        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    private openEditModal(): void {
        this.editModalRef = this.modalService.open(this.editModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static"
        });
    }

    private resetForm(): void {
        this.editForm.reset({
            extract_key: "",
            type: "",
            name: "",
            sql: "",
            customer_code: "",
            field_separator: "",
            connector_id: "",
            remote_directory_path: "",
            remote_bucket: "",
            destination_account_id: "",
            destination_iam_role: "",
            is_enabled: true
        });
    }

    private validateType(): string | null {
        const v = this.editForm.getRawValue();
        if (v.type === EXTRACT_TYPE_SFTP) {
            if (!v.connector_id?.trim() || !v.remote_directory_path?.trim()) {
                return `Connector and remote directory path are required for ${EXTRACT_TYPE_SFTP}.`;
            }
        } else if (v.type === EXTRACT_TYPE_S3) {
            if (!v.remote_bucket?.trim() || !v.destination_account_id?.trim() || !v.destination_iam_role?.trim()) {
                return `Remote bucket, destination account id and destination IAM role are required for ${EXTRACT_TYPE_S3}.`;
            }
        }
        return null;
    }

    private loadConnectors(): void {
        this.apiService.getEdi997ConfigConnectors().subscribe({
            next: (connectors: Edi997ConfigConnector[]) => {
                this.availableConnectors = connectors;
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load connectors");
                console.error("Error loading connectors:", error);
            }
        });
    }

    private refreshGrid(): void {
        this.globalService.showWaitOverlay("Refreshing grid...");
        this.apiService.getExtractConfigEntries().subscribe({
            next: (entries: ExtractConfigEntry[]) => {
                this.service.data = entries;
                this.service.refreshDataSource();
                this.globalService.hideWaitOverlay();
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to refresh extracts");
                this.globalService.hideWaitOverlay();
                console.error("Error refreshing extracts:", error);
            }
        });
    }
}
